// Lib
import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { getFilenameFromUrl } from '../imageModalFileUtils';
import { getElementId, getFileMimeType, getImageProp } from '../../../../../common/elements/utils/elementPropertyUtils';
import { getImageSource } from '../../imageHelper';
import { isPlatformAndroid } from '../../../../platform/utils/platformDetailsUtils';

// Components
import Icon from '../../../../components/icons/Icon';
import { getLargestImageSize } from '../imageModalUtils';
import ModalToolbarButton from '../modalToolbar/ModalToolbarButton';
import ModalToolbarDownloadButton from '../modalToolbar/ModalToolbarDownloadButton';
import ModalToolbarShareButton from '../modalToolbar/ModalToolbarShareButton';
import DownloadOpenLinkInBrowserButton from '../../../../components/buttons/DownloadOpenLinkInBrowserButton';
import { GroupTranslatorProvider, useGroupTranslator } from '../../../../components/i18n/GroupTranslationContext';

// Selectors
import { getIsFeatureEnabledForCurrentUser } from '../../../feature/elementFeatureSelector';
import { getPlatformDetailsSelector } from '../../../../platform/platformSelector';

// Types
import { ImMNElement } from '../../../../../common/elements/elementModelTypes';

// Constants
import { IMAGE_MODAL_MODES } from '../imageModalConstants';
import { ExperimentId } from '../../../../../common/experiments/experimentsConstants';

// Styles
import './ImageModalViewToolbar.scss';

type ImageModalViewToolbarProps = {
    element: ImMNElement;
    isEditable: boolean;
    isPresentationMode: boolean;
    isEditingDisabled: boolean;
    setMode: (mode: string) => void;
    renderSecureImagesThroughProxy: boolean;
    renderSecureImagesThroughCF: boolean;
};

const ImageModalViewToolbarContent = (props: ImageModalViewToolbarProps): ReactElement => {
    const { element, isEditable, setMode, isPresentationMode } = props;
    const translate = useGroupTranslator();

    const elementId = getElementId(element);
    const imageType = getFileMimeType(element) || 'image/jpeg';
    const imageDetails = getImageProp(element);

    const renderSecureImagesThroughProxy = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughProxy),
    );
    const renderSecureImagesThroughCF = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF),
    );
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const isAndroid = isPlatformAndroid(platformDetails);

    const largestImageUrl = getImageSource({
        imageSize: getLargestImageSize(imageDetails),
        imageDetails,
        elementId,

        useSecureMediaUrl: true,
        optimizeForWebRender: false,

        renderSecureImagesThroughProxy,
        renderSecureImagesThroughCF,
    });

    return (
        <div className="ImageModalViewToolbar">
            {!isPresentationMode && (
                <ModalToolbarDownloadButton
                    url={largestImageUrl}
                    filename={getFilenameFromUrl(largestImageUrl, imageType)}
                    elementId={elementId}
                >
                    {translate('download')}
                </ModalToolbarDownloadButton>
            )}
            {isAndroid && (
                <ModalToolbarShareButton
                    url={largestImageUrl}
                    filename={getFilenameFromUrl(largestImageUrl, imageType)}
                    elementId={elementId}
                >
                    {translate('share')}
                </ModalToolbarShareButton>
            )}
            <DownloadOpenLinkInBrowserButton url={largestImageUrl}>
                <ModalToolbarButton>
                    <Icon name="modal-toolbar-open-new-tab" />
                    <div className="text">
                        {translate('newTab')}
                    </div>
                </ModalToolbarButton>
            </DownloadOpenLinkInBrowserButton>
            {isEditable && (
                <ModalToolbarButton onClickFn={() => setMode(IMAGE_MODAL_MODES.DRAW)}>
                    <Icon name="modal-toolbar-draw" />
                    <div className="text">
                        {translate('draw')}
                    </div>
                </ModalToolbarButton>
            )}
            {isEditable && !isPresentationMode && (
                <ModalToolbarButton onClickFn={() => setMode(IMAGE_MODAL_MODES.CROP)}>
                    <Icon name="modal-toolbar-crop" />
                    <div className="text">
                        {translate('cropRotate')}
                    </div>
                </ModalToolbarButton>
            )}
        </div>
    );
};

const ImageModalViewToolbar = (props: ImageModalViewToolbarProps): ReactElement => {
    return (
        <GroupTranslatorProvider group="popup" keyPrefix="imageModal">
            <ImageModalViewToolbarContent {...props} />
        </GroupTranslatorProvider>
    );
};

export default ImageModalViewToolbar;
